import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST, IsDSM, IsEmployee } from 'config/config';
import { BlobStorageTypeEnumValue } from 'enums/BlobStorageTypeEnum';
import { useSelector } from 'react-redux';
import useToast from 'services/Hooks/useToast';
import tableDataQueryStringBuilder from 'services/Utilities/TableData';

const normalizeDealerIds = dealerIds => dealerIds?.filter(Boolean) ?? [];

export const usePrefetchExteriorSignageQuote = () => {
  const queryClient = useQueryClient();
  queryClient.prefetchQuery({
    queryKey: ['exteriorSignageQuote'],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/exterior-signage-quotes/active');
      return response.data;
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

export const useExteriorSignageQuotes = (tableDataParam, dealerIds = []) => {
  const { errorMessage } = useToast();
  const impersonationDealerId = useSelector(state => state.AccountReducer?.profile?.temporaryDealer_Id);
  const normalizedDealerIds = normalizeDealerIds(dealerIds);
  const requestParams = normalizedDealerIds.length ? { ...tableDataParam, dealerIds: normalizedDealerIds } : tableDataParam;

  const query = useQuery({
    queryKey: [
      'exteriorSignageQuotes',
      JSON.stringify(tableDataParam),
      IsDSM || IsEmployee ? impersonationDealerId : 'default',
      normalizedDealerIds.join('|'),
    ],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/exterior-signage-quotes', {
        paramsSerializer: params => tableDataQueryStringBuilder(params),
        params: requestParams,
      });
      return response.data;
    },
    throwOnError: errorMessage,
    staleTime: 0,
  });
  return query;
};

export const useActiveExteriorSignageQuote = isLogged => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['exteriorSignageQuote'],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/exterior-signage-quotes/active');
      return response.data;
    },
    throwOnError: errorMessage,
    enabled: !!isLogged,
  });
  return query;
};

export const useExteriorSignageQuote = exteriorSignageQuoteId => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['historyExteriorSignageQuote', exteriorSignageQuoteId],
    queryFn: async () => {
      if (exteriorSignageQuoteId) {
        const response = await axios.get(`${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuoteId}`);
        return response.data;
      } else {
        return null;
      }
    },
    throwOnError: errorMessage,
  });
  return query;
};

export const usePostExteriorSignageQuoteItem = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addExteriorSignageQuoteItem'],
    mutationFn: async exteriorSignageQuoteItem => {
      return await axios.post(
        `${HOST}/api/dealer-portal/exterior-signage-quotes/items`,
        isDemo ? { ...exteriorSignageQuoteItem, isDemo } : exteriorSignageQuoteItem,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostExteriorSignageQuoteCheckout = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addExteriorSignageQuoteItem'],
    mutationFn: async ({ id, areTermsAccepted }) => {
      return await axios.post(`${HOST}/api/dealer-portal/exterior-signage-quotes/${id}/checkout`, {
        areTermsAccepted,
      });
    },
    onSuccess: () => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], null);
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuote'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostChangeExteriorSignageQuoteItemQuantity = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['changeQuantityExteriorSignageQuoteItem'],
    mutationFn: async exteriorSignageQuoteItem => {
      return await axios.post(
        `${HOST}/api/dealer-portal/exterior-signage-quotes/items/${exteriorSignageQuoteItem.id}/quantity`,
        exteriorSignageQuoteItem,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchExteriorSignageQuoteChangeShipping = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['exteriorSignageQuoteChangeShipping'],
    mutationFn: async exteriorSignageQuote => {
      return await axios.patch(
        `${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuote.id}/shipping`,
        exteriorSignageQuote,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchExteriorSignageQuoteChangeInformation = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['exteriorSignageQuoteChangeInformation'],
    mutationFn: async exteriorSignageQuote => {
      return await axios.patch(
        `${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuote.id}/information`,
        exteriorSignageQuote,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchApproveExteriorSignageQuote = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['approveExteriorSignageQuote'],
    mutationFn: async exteriorSignageQuote => {
      return await axios.patch(
        `${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuote.id}/status`,
        exteriorSignageQuote,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuotes'] });
      queryClient.setQueryData(['historyExteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteExteriorSignageQuoteItem = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteExteriorSignageQuoteItem'],
    mutationFn: async exteriorSignageQuoteItem => {
      return await axios.delete(`${HOST}/api/dealer-portal/exterior-signage-quotes/items/${exteriorSignageQuoteItem.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteExteriorSignageQuote = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteExteriorSignageQuote'],
    mutationFn: async exteriorSignageQuote => {
      return await axios.delete(`${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuote.id}`);
    },
    onSuccess: () => {
      deleteMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], null);
    },
    onError: errorMessage,
  });
  return query;
};

export const useUpdateExteriorSignageQuoteFile = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['updateExteriorSignageQuoteFile'],
    mutationFn: async exteriorSignageQuoteInfo => {
      var formData = new FormData();
      exteriorSignageQuoteInfo.files.forEach(f => formData.append(`files`, f));

      formData.append('type', BlobStorageTypeEnumValue.ExteriorSignageQuote);

      if (exteriorSignageQuoteInfo.exteriorSignageQuoteType) {
        formData.append('ExteriorSignageQuoteType', exteriorSignageQuoteInfo.exteriorSignageQuoteType);
      }

      if (exteriorSignageQuoteInfo.exteriorSignageQuoteItemId) {
        formData.append('exteriorSignageQuoteItemId', exteriorSignageQuoteInfo.exteriorSignageQuoteItemId);
      }

      return await axios.post(
        `${HOST}/api/dashboard/attached-files/${exteriorSignageQuoteInfo.exteriorSignageQuoteId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuote'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteExteriorSignageQuoteFile = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteExteriorSignageQuoteFile'],
    mutationFn: async ({ exteriorSignageQuoteId, photoId }) => {
      return await axios.delete(
        `${HOST}/api/dashboard/attached-files/${exteriorSignageQuoteId}/${photoId}?type=${BlobStorageTypeEnumValue.ExteriorSignageQuote}`,
      );
    },
    onSuccess: () => {
      deleteMessage();
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuote'] });
    },
    onError: errorMessage,
  });
  return query;
};

// Section BRP Club
export const usePostBrpClubExteriorSignageQuoteItem = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addBrpClubExteriorSignageQuoteItem'],
    mutationFn: async exteriorSignageQuoteItem => {
      return await axios.post(
        `${HOST}/api/dealer-portal/brp-club/exterior-signage-quotes/items`,
        isDemo ? { ...exteriorSignageQuoteItem, isDemo } : exteriorSignageQuoteItem,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};
